import { useState } from "react";
import { Reveal, PillButton } from "./ui";
import { sisterQuestions, scoreSister, sisterResults } from "./quizData";
import { postSisterQuiz } from "./api";

const taglines = {
  mayor: "You don't just love Sash. You live here.",
  lurker: "Watching quietly — emerging only when something really good drops.",
  shopper: "Show you the bag. If you like the bag, you buy the bag.",
  collector: "Your Sashes may require their own organizational infrastructure.",
  evangelist: "You didn't choose the Sash evangelist life. It chose you.",
  unicorn: "One Sash. You love it. It still works. Fascinating, honestly.",
};

function drawWrapped(ctx, text, cx, y, maxW, lh) {
  const words = text.split(" ");
  let line = "", yy = y;
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxW && line) { ctx.fillText(line, cx, yy); line = w; yy += lh; }
    else line = test;
  }
  if (line) { ctx.fillText(line, cx, yy); yy += lh; }
  return yy;
}

async function makeShareCard(resultKey) {
  const r = sisterResults[resultKey];
  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  try { await document.fonts.ready; } catch {}

  ctx.fillStyle = "#12211f"; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(255,255,255,0.14)"; ctx.lineWidth = 2;
  ctx.strokeRect(64, 64, W - 128, H - 128);
  ctx.textAlign = "center";

  ctx.fillStyle = "#bcd0cb"; ctx.font = "700 30px 'Hanken Grotesk', sans-serif";
  try { ctx.letterSpacing = "8px"; } catch {}
  ctx.fillText("SASH · THE NEXT ERA IS LOADING", W / 2, 190);
  try { ctx.letterSpacing = "0px"; } catch {}

  ctx.fillStyle = "#1a8a7e"; ctx.font = "700 44px 'Hanken Grotesk', sans-serif";
  try { ctx.letterSpacing = "10px"; } catch {}
  ctx.fillText("YOU ARE", W / 2, 720);
  try { ctx.letterSpacing = "0px"; } catch {}

  ctx.fillStyle = "#f7efe3"; ctx.font = "500 100px 'Fraunces', serif";
  let y = drawWrapped(ctx, r.title, W / 2, 860, W - 200, 110);

  ctx.fillStyle = "#d8c3a3"; ctx.font = "italic 400 48px 'Fraunces', serif";
  drawWrapped(ctx, taglines[resultKey], W / 2, y + 80, W - 260, 64);

  ctx.fillStyle = "#bcd0cb"; ctx.font = "italic 500 46px 'Fraunces', serif";
  ctx.fillText("What kind of Sash Sister are you?", W / 2, H - 300);
  ctx.fillStyle = "#f7efe3"; ctx.font = "700 36px 'Hanken Grotesk', sans-serif";
  try { ctx.letterSpacing = "6px"; } catch {}
  ctx.fillText("SASHBAG.COM", W / 2, H - 200);
  try { ctx.letterSpacing = "0px"; } catch {}

  const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
  const file = new File([blob], `sash-sister-${resultKey}.png`, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try { await navigator.share({ files: [file], title: r.title }); return; } catch {}
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
}

export const SisterQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [first, setFirst] = useState("");
  const [email, setEmail] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const total = sisterQuestions.length;
  const choose = (opt) => {
    const next = [...answers.slice(0, step), opt];
    setAnswers(next);
    if (step + 1 < total) setStep(step + 1);
    else setResult(scoreSister(next).result);
  };
  const restart = () => { setStep(0); setAnswers([]); setResult(null); setSaved(false); setFirst(""); setEmail(""); };
  const save = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSaving(true);
    try { await postSisterQuiz({ first_name: first, email, result, answers }); setSaved(true); } catch {} finally { setSaving(false); }
  };
  const share = async () => {
    const r = sisterResults[result];
    const text = `I'm ${r.title} in the Sash universe. What kind of Sash Sister are you? sashbag.com`;
    try {
      if (navigator.share) { await navigator.share({ text }); return; }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true); setTimeout(() => setCopied(false), 1800); return;
      }
      throw new Error("unsupported");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true); setTimeout(() => setCopied(false), 1800);
      } catch { /* user cancelled or blocked; ignore */ }
    }
  };

  const q = sisterQuestions[step];
  const r = result ? sisterResults[result] : null;
  return (
    <section id="sister-quiz" className="section-dark grain relative px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="sister-quiz-section">
      <div className="max-w-[760px] mx-auto relative z-10">
        <Reveal>
          <div className="text-center">
            <span className="eyebrow">Play · Just For Fun</span>
            <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.2rem)] leading-[1.03] text-[var(--cream)]">
              What kind of <span className="italic" style={{ color: "var(--tan)" }}>Sash Sister</span> are you?
            </h2>
            <p className="mt-4 text-[1.05rem] text-[var(--sand)]">
              Scientifically meaningless. Emotionally devastating. Let's find out.
            </p>
          </div>
        </Reveal>

        <div className="result-card mt-10" data-testid="sister-quiz-card">
          {!result ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-bold text-[var(--tan-deep)]">Question {step + 1} of {total}</span>
                {step > 0 && (<button className="text-sm text-[var(--ink-soft)] underline" onClick={() => setStep(step - 1)} data-testid="sister-quiz-back">Back</button>)}
              </div>
              <div className="qp-bar mb-7"><div className="qp-fill" style={{ width: `${(step / total) * 100}%` }} /></div>
              <h3 className="sash-serif text-2xl md:text-3xl leading-tight mb-6">{q.q}</h3>
              <div className="space-y-3">
                {q.options.map((o) => (
                  <button key={o.k} className={`quiz-opt ${answers[step] && answers[step].k === o.k ? "sel" : ""}`} onClick={() => choose(o)} data-testid={`sister-quiz-opt-${step}-${o.k}`}>
                    <span className="qk">{o.k}</span>{o.t}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="sister-quiz-result">
              <span className="eyebrow">You are…</span>
              <h3 className="mt-3 sash-serif text-[clamp(1.8rem,4.5vw,2.8rem)] leading-tight" style={{ color: "var(--tan-deep)" }}>{r.title}</h3>
              <p className="mt-4 text-[1.05rem] leading-relaxed text-[var(--ink-soft)]">{r.blurb}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="#product-quiz" className="cta" data-testid="sister-quiz-to-product">Find My Perfect Sash →</a>
                <a href="#relaunch-final" className="cta cta-ghost" data-testid="sister-quiz-to-relaunch">Join the Relaunch List →</a>
                <PillButton onClick={() => makeShareCard(result)} variant="ghost" testid="sister-quiz-savecard">Save Story Card</PillButton>
                <PillButton onClick={share} variant="ghost" testid="sister-quiz-share">{copied ? "Copied!" : "Share My Result"}</PillButton>
              </div>

              {saved ? (
                <p className="mt-8 sash-serif italic text-xl text-[var(--tan-deep)]" data-testid="sister-quiz-saved">You're on the list! ✨</p>
              ) : (
                <form onSubmit={save} className="mt-8 pt-6 border-t border-[var(--line)]" data-testid="sister-quiz-save-form">
                  <p className="mb-3 font-bold">Want first dibs when Sash comes back?</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input className="sf-input sm:max-w-[150px]" placeholder="First name" value={first} onChange={(e) => setFirst(e.target.value)} data-testid="sister-quiz-firstname" />
                    <input className="sf-input flex-1" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="sister-quiz-email" />
                    <PillButton type="submit" testid="sister-quiz-save">{saving ? "…" : "Put Me On The List"}</PillButton>
                  </div>
                </form>
              )}
              <button className="mt-6 text-sm text-[var(--ink-soft)] underline" onClick={restart} data-testid="sister-quiz-restart">Retake the quiz</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
